import type { Service } from "@mirage/service-fss-native/service";
import provideFSSNativeService from "@mirage/service-fss-native/service";
import { tagged } from "@mirage/service-logging";
import { set } from "@mirage/service-settings";
import { isEsvConfig } from "@mirage/shared/util/esv";

import { dashEsvControllerKeys } from "@mirage/service-fss-native/hooks/nativeApi";
import type {
  CurrentDesktopAccount,
  DashEsvControllerWithEventService,
  DashIntegrationEventMap,
  FenixWindow,
  MessagePayload,
  NativeSettings,
} from "@mirage/service-fss-native/types";

const logger = tagged("fss-native-handlers");

export class FSSNativeAdapter implements Service {
  private nativeApi: DashEsvControllerWithEventService | undefined;
  private _initializationPromise: Promise<void>;
  private initializationComplete = false;
  private settingsUnregister: (() => void) | undefined;

  constructor() {
    this._initializationPromise = this.initializeNativeApi();
  }

  private async initializeNativeApi(): Promise<void> {
    if (!isEsvConfig()) {
      logger.info("Not in ESV environment");
      return;
    }

    try {
      const fenixWindow = await this.getFenixWindow();
      if (!(fenixWindow && fenixWindow.controller)) {
        logger.error("fenixWindow.controller is not available");
        return;
      }

      for (const key of dashEsvControllerKeys) {
        if (typeof fenixWindow.controller[key] !== "function") {
          logger.error(
            `Required method ${String(key)} is missing or not a function on fenixWindow.controller`,
          );
        }
      }

      const combinedApi: DashEsvControllerWithEventService = {
        ...fenixWindow.controller,
        addDashEsvEventListener: fenixWindow.dashIntegrationService
          ? fenixWindow.dashIntegrationService.addEventListener
          : () => () => {},
      };

      this.nativeApi = combinedApi;

      await this.setupSettingsListener();
      this.initializationComplete = true;
      logger.info("FSS Native API initialized successfully");
    } catch (error) {
      logger.error("Failed to initialize FSS Native API", error);
    }
  }

  private async setupSettingsListener(): Promise<void> {
    if (!this.nativeApi?.addDashEsvEventListener) {
      logger.warn(
        "Cannot setup keyboard shortcut / settings listener - addDashEsvEventListener not available",
      );
      return;
    }

    try {
      // Try new getSettings API first, fall back to getShortcutValue if not available
      if (typeof this.nativeApi.getSettings === "function") {
        await this.setupNewSettingsListener();
      } else {
        logger.info(
          "getSettings not available, falling back to getShortcutValue for keyboard shortcuts",
        );
        await this.setupLegacyShortcutListener();
      }
    } catch (error) {
      logger.error(
        "Failed to setup keyboard shortcut / settings listener",
        error,
      );
      // If getSettings fails, try fallback to legacy approach
      if (typeof this.nativeApi.getShortcutValue === "function") {
        logger.info("Attempting fallback to legacy keyboard shortcut listener");
        await this.setupLegacyShortcutListener();
      }
    }
  }

  private async applyNativeSettings(settings: NativeSettings): Promise<void> {
    if (settings.appShortcut) {
      await set("appShortcut", settings.appShortcut);
    }
    if (settings.persistentAppShortcut) {
      await set("persistentAppShortcut", settings.persistentAppShortcut);
    }
  }

  private async setupNewSettingsListener(): Promise<void> {
    if (!this.nativeApi) return;

    const settings = await this.nativeApi.getSettings();
    await this.applyNativeSettings(settings);

    this.settingsUnregister = this.nativeApi.addDashEsvEventListener(
      "SettingsUpdated",
      async (data: { updatedSettings: NativeSettings }) => {
        if (!data || !data.updatedSettings) {
          logger.info(
            "No data or updated settings / keyboard shortcuts in SettingsUpdated event",
          );
          return;
        }

        await this.applyNativeSettings(data.updatedSettings);
        logger.debug(
          `Settings / keyboard shortcuts updated using: ${JSON.stringify(data.updatedSettings)}`,
        );
      },
    );
    logger.info("Settings / keyboard shortcuts listener set up successfully");
  }

  private async setupLegacyShortcutListener(): Promise<void> {
    if (!this.nativeApi) return;

    const shortcutValue = await this.nativeApi.getShortcutValue();
    await set("appShortcut", shortcutValue);

    this.settingsUnregister = this.nativeApi.addDashEsvEventListener(
      "KeyboardShortcutUpdated",
      async (data: { newShortcut: string }) => {
        await set("appShortcut", data.newShortcut);
        logger.debug(`Keyboard shortcut updated to: ${data.newShortcut}`);
      },
    );
    logger.info("Keyboard shortcut listener set up successfully");
  }

  private async getFenixWindow(): Promise<FenixWindow | undefined> {
    const fenixWindow = window as unknown as FenixWindow;
    if (
      fenixWindow.fenixInitializationComplete === null ||
      fenixWindow.fenixInitializationComplete === undefined
    ) {
      logger.error(
        "Fenix window platform check is not available on window object",
      );
      return undefined;
    }
    await fenixWindow.fenixInitializationComplete;
    return fenixWindow;
  }

  private async isInitialized(): Promise<void> {
    if (this.initializationComplete) {
      return;
    }
    await this._initializationPromise;
  }

  public getCurrentAccount = async (): Promise<CurrentDesktopAccount> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.getCurrentAccount();
  };

  public reloadView = async (): Promise<boolean> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.reloadView();
  };

  public showDashWindow = async (): Promise<void> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.showDashWindow();
  };

  public hideDashWindow = async (): Promise<void> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.hideDashWindow();
  };

  public getAccessToken = async (): Promise<string> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.getAccessToken();
  };

  public getShortcutValue = async (): Promise<string> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.getShortcutValue();
  };

  public getFeatureSessionId = async (): Promise<string> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available");
    }
    return this.nativeApi.getFeatureSessionId();
  };

  public addDashEsvEventListener = async (
    eventName: keyof DashIntegrationEventMap,
    callback: (
      args: DashIntegrationEventMap[keyof DashIntegrationEventMap],
    ) => void,
  ): Promise<() => void> => {
    await this.isInitialized();
    if (!this.nativeApi?.addDashEsvEventListener) {
      logger.warn("addDashEsvEventListener not available in native API");
      return () => {};
    }
    return this.nativeApi.addDashEsvEventListener(eventName, callback);
  };

  public sendMessageToRenderer = async (
    message: MessagePayload,
  ): Promise<void> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error(
        "Native API not available when sending message to renderer",
      );
    }
    return this.nativeApi.sendMessageToRenderer(message);
  };

  public launchPersistentWindow = async (): Promise<void> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error(
        "Native API not available when launching persistent window",
      );
    }
    return this.nativeApi.launchPersistentWindow();
  };

  public getViewName = async (): Promise<string> => {
    await this.isInitialized();
    if (!this.nativeApi) {
      throw new Error("Native API not available when getting view name");
    }
    return this.nativeApi.getViewName();
  };

  public cleanup(): void {
    if (this.settingsUnregister) {
      this.settingsUnregister();
      this.settingsUnregister = undefined;
      logger.info("Settings/shortcut listener cleaned up");
    }
  }
}

function createNoopService(): Service {
  const throwError = (method: string) => {
    throw new Error(
      `FSS Native is not available in this environment. Called method: ${method}`,
    );
  };

  return {
    getCurrentAccount: async () => throwError("getCurrentAccount"),
    reloadView: async () => throwError("reloadView"),
    showDashWindow: async () => throwError("showDashWindow"),
    getFeatureSessionId: async () => throwError("getFeatureSessionId"),
    hideDashWindow: async () => throwError("hideDashWindow"),
    launchPersistentWindow: async () => throwError("launchPersistentWindow"),
    getAccessToken: async () => throwError("getAccessToken"),
    getShortcutValue: async () => throwError("getShortcutValue"),
    addDashEsvEventListener: async () => throwError("addDashEsvEventListener"),
    sendMessageToRenderer: async (message) =>
      throwError(`sendMessageToRenderer ${JSON.stringify(message)}`),
    getViewName: async () => throwError("getViewName"),
  };
}

export function setupFSSNativeHandlers() {
  let service: Service;

  if (!isEsvConfig()) {
    // only expose service methods if in ESV environment
    service = createNoopService();
  } else {
    service = new FSSNativeAdapter();
  }
  provideFSSNativeService(service);
}
